import { Controller } from '@hotwired/stimulus'

// Каталог маленький (десятки товаров) — весь список подсказок отдаётся сразу
// со страницей (ComparesController#new), а поиск фильтрует его на клиенте.
// Без этого лимита при пустом запросе на экране была бы вся выдача разом.
const RESULTS_LIMIT = 8

/**
 * @file Конструктор сравнения ракеток: строка поиска добавляет ракетки в
 * слоты (2-4), кнопка «Compare» ведёт на страницу результата — состояние
 * целиком в её URL (см. ComparesController#show). Список для поиска (все
 * видимые товары) приходит один раз при открытии страницы, само нажатие
 * клавиши на сервер не ходит.
 *
 * Выпадашка подсказок переиспользует разметку/классы search-suggestions__*
 * из search_form_controller.js — тот же визуальный язык, но выбор здесь не
 * уводит со страницы, а заполняет слот.
 */
export default class ComparePickerController extends Controller {
  static targets = ['input', 'results', 'slots', 'submit']

  static values = {
    products: Array,
    comparePath: String,
    maxSlots: { type: Number, default: 3 },
    minSlots: { type: Number, default: 2 },
    initial: Object,
    optionalLabel: String,
    addLabel: String,
    removeLabel: String
  }

  #selected = []
  #entries = []

  // Не element.contains: внутри контроллера есть и другие пустые слоты — по
  // виду часть "пустого места" страницы, а по DOM всё ещё сам виджет. Закрываем
  // по клику вне строки поиска и самой выпадашки, а не вне виджета целиком.
  //
  // Клик по пустому слоту ("+") тоже исключаем: он сам открывает выпадашку
  // через focusSearch, а этот же клик всплывает сюда следом — без исключения
  // обработчик закрывал бы её в тот же момент, что и открытие.
  #handleOutsideClick = (event) => {
    if (this.inputTarget.contains(event.target)) return
    if (this.hasResultsTarget && this.resultsTarget.contains(event.target))
      return
    if (event.target.closest('[data-action*="focusSearch"]')) return

    this.#closeResults()
  }

  connect() {
    if (this.hasInitialValue && this.initialValue?.slug) {
      this.#selected = [this.initialValue]
    }
    this.#renderSlots()
    document.addEventListener('click', this.#handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener('click', this.#handleOutsideClick)
  }

  // Фильтрация целиком локальная — ни дебаунса, ни похода на сервер не нужно.
  search() {
    this.#renderResults(this.#filter(this.inputTarget.value))
  }

  // Клик по пустому слоту ("+"): фокусируем строку поиска и сразу открываем
  // выпадашку — не заставляем сначала догадаться, что нужно печатать.
  focusSearch() {
    this.inputTarget.focus()
    this.#renderResults(this.#filter(this.inputTarget.value))
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    const entry = this.#entries[index]
    if (!entry) return

    this.#addSlot(entry)
  }

  remove(event) {
    const slug = event.currentTarget.dataset.slug
    this.#selected = this.#selected.filter((item) => item.slug !== slug)
    this.#renderSlots()
  }

  submit(event) {
    if (this.#selected.length < this.minSlotsValue) event.preventDefault()
  }

  // Токены запроса должны встретиться в названии или бренде все разом (порядок
  // не важен) — так «head coello» находит «HEAD Coello Pro» вне зависимости
  // от того, в каком поле какое слово. Уже выбранные слоты из выдачи убираем:
  // выбрать одну ракетку дважды нельзя.
  #filter(rawQuery) {
    const excluded = new Set(this.#selected.map((item) => item.slug))
    const available = this.productsValue.filter(
      (item) => !excluded.has(item.slug)
    )

    const tokens = rawQuery.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (tokens.length === 0) return available.slice(0, RESULTS_LIMIT)

    return available
      .filter((item) => {
        const haystack = `${item.name} ${item.brand || ''}`.toLowerCase()
        return tokens.every((token) => haystack.includes(token))
      })
      .slice(0, RESULTS_LIMIT)
  }

  #addSlot(entry) {
    if (this.#selected.some((item) => item.slug === entry.slug)) return
    if (this.#selected.length >= this.maxSlotsValue) return

    this.#selected.push(entry)
    this.inputTarget.value = ''
    this.#closeResults()
    this.#renderSlots()
  }

  #renderResults(entries) {
    this.#entries = entries
    if (!this.hasResultsTarget) return

    const dropdown = this.resultsTarget
    dropdown.innerHTML = ''

    if (entries.length === 0) {
      this.#closeResults()
      return
    }

    entries.forEach((entry, index) =>
      dropdown.appendChild(this.#buildResultItem(entry, index))
    )
    dropdown.classList.remove('hidden')
  }

  #buildResultItem(entry, index) {
    const item = document.createElement('button')
    item.type = 'button'
    item.className = 'search-suggestions__item'
    // <button> не растягивается на всю ширину даже с display: flex (в отличие
    // от <a>, которым строит те же .search-suggestions__item общий поиск) —
    // без этого подсветка при наведении/клавиатуре обрывается на середине.
    item.style.width = '100%'
    item.dataset.index = String(index)
    item.dataset.action = 'click->compare-picker#select'

    if (entry.thumbnail_url) {
      const image = document.createElement('img')
      image.className = 'search-suggestions__image'
      image.src = entry.thumbnail_url
      image.alt = ''
      image.width = 40
      image.height = 40
      item.appendChild(image)
    }

    const body = document.createElement('span')
    body.className = 'search-suggestions__body'

    const name = document.createElement('span')
    name.className = 'search-suggestions__name'
    name.textContent = entry.name
    body.appendChild(name)

    if (entry.brand) {
      const subtitle = document.createElement('span')
      subtitle.className = 'search-suggestions__subtitle'
      subtitle.textContent = entry.brand
      body.appendChild(subtitle)
    }

    item.appendChild(body)
    return item
  }

  #closeResults() {
    this.#entries = []
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ''
      this.resultsTarget.classList.add('hidden')
    }
  }

  #renderSlots() {
    if (!this.hasSlotsTarget) return

    const slots = this.slotsTarget
    slots.innerHTML = ''

    this.#selected.forEach((item) =>
      slots.appendChild(this.#buildFilledSlot(item))
    )
    for (let i = this.#selected.length; i < this.maxSlotsValue; i++) {
      slots.appendChild(this.#buildEmptySlot(i === this.#selected.length))
    }

    this.#updateSubmit()
  }

  #buildFilledSlot(item) {
    const slot = document.createElement('div')
    slot.className = 'compare-slot relative flex flex-col'

    const imageWrap = document.createElement('div')
    imageWrap.className =
      'relative aspect-square rounded-3xl overflow-hidden bg-bg-page border border-border'

    if (item.rating_avg !== null) {
      const rating = document.createElement('span')
      rating.className =
        'absolute top-5 left-5 z-10 flex items-center justify-center min-w-9 h-9 px-2 rounded-full border border-border bg-bg-page text-sb-16 text-text-blue shadow-sm'
      rating.textContent = item.rating_avg
      imageWrap.appendChild(rating)
    }

    const remove = document.createElement('button')
    remove.type = 'button'
    remove.className =
      'absolute top-5 right-5 z-10 flex items-center justify-center w-9 h-9 rounded-full border border-border bg-bg-page text-icon-stroke-tertiary shadow-sm'
    remove.dataset.slug = item.slug
    remove.dataset.action = 'click->compare-picker#remove'
    remove.setAttribute('aria-label', this.removeLabelValue || 'Remove')
    remove.innerHTML =
      '<svg width="14" height="14" fill="none" data-icon="cross"><use href="#icon-cross"></use></svg>'
    imageWrap.appendChild(remove)

    if (item.thumbnail_url) {
      const image = document.createElement('img')
      image.className = 'w-full h-full object-contain p-4'
      image.src = item.thumbnail_url
      image.alt = ''
      imageWrap.appendChild(image)
    }

    slot.appendChild(imageWrap)

    // Прямоугольник без скруглений, отдельным контуром — не продолжение
    // скруглённой карточки картинки, а самостоятельная плашка с названием.
    const body = document.createElement('div')
    body.className = 'p-3 mt-3 border border-border bg-bg-page'

    if (item.brand) {
      const brand = document.createElement('span')
      brand.className =
        'block text-r-12 uppercase tracking-wide text-text-secondary'
      brand.textContent = item.brand
      body.appendChild(brand)
    }

    const name = document.createElement('b')
    name.className = 'block text-sb-16 mt-1 line-clamp-2'
    name.textContent = item.name
    body.appendChild(name)

    slot.appendChild(body)
    return slot
  }

  #buildEmptySlot(isNext) {
    const slot = document.createElement('button')
    slot.type = 'button'
    slot.dataset.action = 'click->compare-picker#focusSearch'
    slot.className =
      'flex flex-col items-center justify-center gap-2 aspect-square rounded-3xl border border-dashed border-border text-text-secondary text-r-14 hover:border-text-blue hover:text-text-blue transition-colors'

    const icon = document.createElement('span')
    icon.className =
      'flex items-center justify-center w-9 h-9 rounded-full bg-bg-gray-subtle text-text-secondary text-2xl leading-none'
    icon.textContent = '+'
    slot.appendChild(icon)

    const label = document.createElement('span')
    label.textContent = isNext ? this.addLabelValue : this.optionalLabelValue
    slot.appendChild(label)

    return slot
  }

  #updateSubmit() {
    if (!this.hasSubmitTarget) return

    const count = this.#selected.length
    const ready = count >= this.minSlotsValue
    const slugs = this.#selected.map((item) => item.slug).join('/')

    this.submitTarget.href = ready
      ? this.comparePathValue.replace('__SLUGS__', slugs)
      : '#'
    this.submitTarget.classList.toggle('opacity-50', !ready)
    this.submitTarget.classList.toggle('pointer-events-none', !ready)
    this.submitTarget.setAttribute('aria-disabled', String(!ready))
  }
}
